import { useState, useMemo } from "react";

const getStatusBadgeColor = (status) => {
  if (status === "ProtectionConfigured")
    return "bg-green-500/20 text-green-400";
  if (status === "BackupsSuspended") return "bg-yellow-500/20 text-yellow-400";
  if (status === "ProtectionStopped") return "bg-red-500/20 text-red-400";
  return "bg-gray-500/20 text-gray-400";
};

const calculateEndTime = (startTime, duration) => {
  if (!startTime || !duration) return "";

  try {
    const timePart = startTime.split("T")[1];
    const [hours, minutes, seconds] = timePart.split(":");
    const startDate = new Date();
    startDate.setHours(parseInt(hours), parseInt(minutes), parseFloat(seconds));

    const durationParts = duration.split(":");
    const durationSeconds =
      parseInt(durationParts[0]) * 3600 +
      parseInt(durationParts[1]) * 60 +
      parseFloat(durationParts[2]);

    const endDate = new Date(startDate.getTime() + durationSeconds * 1000);
    return endDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (e) {
    return "";
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatTime = (startTime) => {
  if (!startTime) return "";
  return startTime.split("T")[1]?.split(".")[0] || "";
};

const formatDuration = (duration) => {
  if (!duration) return "";
  const parts = duration.split(":");
  if (parts.length === 3) {
    return `${parts[0]}:${parts[1]}:${Math.floor(parseFloat(parts[2]))}`;
  }
  return duration;
};

const DiskBackupSection = ({ diskData }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasHistory =
    diskData.backupHistory && diskData.backupHistory.length > 0;
  const isScrollable = hasHistory && diskData.backupHistory.length > 7;

  const historyWithEndTime = useMemo(() => {
    if (!hasHistory) return [];
    return diskData.backupHistory.map((record) => ({
      ...record,
      formattedDate: formatDate(record.date),
      startTimeFormatted: formatTime(record.startTime),
      durationFormatted: formatDuration(record.duration),
      endTime: calculateEndTime(record.startTime, record.duration),
      statusFormatted: record.status,
    }));
  }, [diskData.backupHistory, hasHistory]);

  return (
    <div
      className={`bg-primary-dark rounded-2xl p-6 border border-gray-700/50 transition-all`}
    >
      <div
        className="flex justify-between items-center cursor-pointer mb-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-lg font-semibold text-white">
              {diskData.diskName}
            </h3>
          </div>
        </div>
        {hasHistory && (
          <button className="text-gray-400 hover:text-white transition-colors">
            {isExpanded ? "▲" : "▼"}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="mt-3">
          {!hasHistory ? (
            <div className="text-center py-6 bg-primary-dark/30 rounded-lg">
              <p className="text-gray-400 italic">
                No backup history available
              </p>
            </div>
          ) : (
            <div
              className={`overflow-x-auto ${isScrollable ? "max-h-64 overflow-y-auto" : ""}`}
            >
              <table className="min-w-full text-sm">
                <thead className="bg-primary-dark/80 sticky top-0">
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                      Date
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                      Start Time
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                      End Time
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                      Duration
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-semibold">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historyWithEndTime.map((record, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-700/50 hover:bg-primary-dark/40 transition-colors"
                    >
                      <td className="py-2 px-4 text-gray-200">
                        {record.formattedDate}
                      </td>
                      <td className="py-2 px-4 text-gray-200">
                        {record.startTimeFormatted}
                      </td>
                      <td className="py-2 px-4 text-gray-200">
                        {record.endTime}
                      </td>
                      <td className="py-2 px-4 text-gray-200">
                        {record.durationFormatted}
                      </td>
                      <td className="py-2 px-4">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">
                          {record.statusFormatted}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiskBackupSection;